import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar, Style } from '@capacitor/status-bar';
import { APP_URL_SCHEME, isAndroid, isIOS, isNative } from './platform';
import { lockDocumentScroll, pinScrollersToBottom, revealFocusedField, setKeyboardInset } from './keyboard';
import { lockPhonePortrait } from './orientation';
import { supabase } from './supabase';
import { appLinkPath, authCallbackCode, navigateInApp } from './appLinks';

type Theme = 'light' | 'dark';

// Stack of "close me instead" handlers registered by whatever modal/sheet is
// currently on top (BottomSheet, PhotoLightbox, ...). The Android hardware
// back button consults this before falling back to page navigation, so
// pressing back closes the topmost overlay rather than routing underneath it.
const modalBackHandlers: Array<() => void> = [];

/**
 * Register a callback the Android back button should invoke while a modal
 * is open, instead of navigating the underlying route. Returns an
 * unregister function — call it on close/unmount. Safe to call on iOS/web;
 * the stack simply goes unused there since no back-button listener reads it.
 */
export function registerModalBackHandler(onBack: () => void): () => void {
  modalBackHandlers.push(onBack);
  return () => {
    const idx = modalBackHandlers.lastIndexOf(onBack);
    if (idx !== -1) modalBackHandlers.splice(idx, 1);
  };
}

/** Close the on-screen keyboard (native app). On the web, blurring the field does it. */
export async function hideKeyboard(): Promise<void> {
  if (!isNative) return;
  try {
    await Keyboard.hide();
  } catch {
    /* no keyboard up */
  }
}

/**
 * Keep the native status bar legible against the current theme. Called on boot
 * and whenever the user toggles the theme. No-op on the web.
 */
export async function applyStatusBarTheme(theme: Theme): Promise<void> {
  if (!isNative) return;
  try {
    // Style.Dark = light text (for dark backgrounds); Style.Light = dark text.
    await StatusBar.setStyle({ style: theme === 'dark' ? Style.Dark : Style.Light });
    if (isAndroid) {
      // Match --bg exactly (tokens.css) — these used to be close-but-different
      // placeholder values (#0e0e0e / #f8f6f1), which left a visible seam
      // between the status bar and the page content right below it.
      await StatusBar.setBackgroundColor({ color: theme === 'dark' ? '#1C1B19' : '#F4ECD2' });
    }
  } catch {
    // StatusBar can be unavailable on some launch states; ignore.
  }
}

/**
 * Initialise everything that only matters inside the native shell: splash
 * screen, keyboard behaviour, Android back button, and the OAuth deep-link
 * handoff. Safe to call unconditionally — it returns immediately on the web.
 *
 * Returns a cleanup function that removes the listeners it registered.
 */
export async function initNative(getTheme: () => Theme): Promise<() => void> {
  if (!isNative) return () => {};

  const cleanups: Array<() => void> = [];

  // 1. Status bar tracks the current theme.
  await applyStatusBarTheme(getTheme());

  // Phones stay upright (a phone turned sideways is wider than the desktop
  // breakpoint and would get the desktop layout); tablets rotate freely.
  void lockPhonePortrait();

  // 2. Keyboard — see lib/keyboard.ts for the full model. willShow/willHide
  // fire as the keyboard starts moving (on both platforms), which is what
  // lets the layout animate alongside it rather than after it.
  const root = document.documentElement;
  if (isIOS) {
    // Unconditionally, before anything that can throw: with the native scroll
    // view disabled (ios.scrollEnabled: false), #root is the only thing that
    // scrolls, and it only does under kb-inset.
    root.classList.add('kb-inset');
    cleanups.push(() => root.classList.remove('kb-inset'), lockDocumentScroll());
  }
  try {
    let stopPinning = () => {};

    const showHandle = await Keyboard.addListener('keyboardWillShow', ({ keyboardHeight }) => {
      root.classList.add('keyboard-open');
      stopPinning();
      stopPinning = pinScrollersToBottom();
      if (isIOS) setKeyboardInset(keyboardHeight);
    });
    const hideHandle = await Keyboard.addListener('keyboardWillHide', () => {
      root.classList.remove('keyboard-open');
      stopPinning();
      stopPinning = pinScrollersToBottom();
      if (isIOS) setKeyboardInset(0);
    });
    // Android's adjustResize already reveals the focused field itself.
    const didShowHandle = isIOS ? await Keyboard.addListener('keyboardDidShow', revealFocusedField) : null;
    cleanups.push(
      () => void showHandle.remove(),
      () => void hideHandle.remove(),
      () => void didShowHandle?.remove(),
      () => stopPinning(),
    );

    if (isIOS) {
      await Keyboard.setResizeMode({ mode: KeyboardResize.None });
      await Keyboard.setAccessoryBarVisible({ isVisible: false });
    }
  } catch {
    /* keyboard plugin may be absent in some builds */
  }

  // 3. Android hardware back button: navigate back, or exit at the root.
  if (isAndroid) {
    const backHandle = await CapApp.addListener('backButton', ({ canGoBack }) => {
      const topHandler = modalBackHandlers[modalBackHandlers.length - 1];
      if (topHandler) {
        topHandler();
        return;
      }
      if (canGoBack && window.history.length > 1) {
        window.history.back();
      } else {
        void CapApp.exitApp();
      }
    });
    cleanups.push(() => void backHandle.remove());
  }

  // 4. Deep links — the OAuth callback after Google/Apple sign-in, and
  //    https links to the website (e.g. email buttons) that the OS hands to
  //    the app instead of the browser. Fires for cold starts too.
  const urlHandle = await CapApp.addListener('appUrlOpen', ({ url }) => {
    void handleDeepLink(url);
  });
  cleanups.push(() => void urlHandle.remove());

  // 5. Hide the splash screen once the web layer is ready.
  try {
    await SplashScreen.hide();
  } catch {
    /* already hidden */
  }

  return () => cleanups.forEach((fn) => fn());
}

/**
 * Handle an incoming deep link. A website link opens the matching screen in
 * the app (signed-out travelers land on sign-in first, as on the web). For
 * OAuth we close the in-app browser and
 * exchange the returned code for a Supabase session; onAuthStateChange then
 * propagates the new session to the app.
 */
async function handleDeepLink(url: string): Promise<void> {
  const code = authCallbackCode(url, APP_URL_SCHEME);
  if (code === null) {
    const path = appLinkPath(url);
    if (path) navigateInApp(path);
    return;
  }

  try {
    await Browser.close();
  } catch {
    /* browser may already be closed */
  }

  // PKCE only: tokens handed over in a link are never installed as the
  // session (that let a crafted link sign the app into another account).
  try {
    if (code) await supabase.auth.exchangeCodeForSession(code);
  } catch (err) {
    console.error('Failed to handle auth deep link', err);
  }
}
